import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  DollarSign,
  BarChart3,
  FileCheck,
  Target,
  Eye,
  TrendingUp,
  Scale,
  Clock
} from "lucide-react";

const MetricCard = ({ label, value, subtext, icon: Icon, color = "slate" }) => {
  const colorClasses = {
    emerald: "text-emerald-600 bg-emerald-50 border-emerald-200",
    amber: "text-amber-600 bg-amber-50 border-amber-200",
    red: "text-red-600 bg-red-50 border-red-200",
    blue: "text-blue-600 bg-blue-50 border-blue-200",
    purple: "text-purple-600 bg-purple-50 border-purple-200",
    slate: "text-slate-600 bg-slate-50 border-slate-200",
  };
  
  return (
    <div className={`border rounded-md p-3 ${colorClasses[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} />
        <span className="text-sm uppercase tracking-wider font-bold">{label}</span>
      </div>
      <div className="text-xl font-bold">{value}</div>
      {subtext && <div className="text-sm opacity-70">{subtext}</div>}
    </div>
  );
};

const RiskBadge = ({ level }) => {
  const styles = {
    low: "bg-emerald-100 text-emerald-700",
    medium: "bg-amber-100 text-amber-700",
    high: "bg-red-100 text-red-700",
  };
  return (
    <Badge className={`text-sm ${styles[level] || styles.medium}`}>
      {level?.toUpperCase()} RISK
    </Badge>
  );
};

const Section = ({ title, icon: Icon, children, color = "slate" }) => {
  const bgColors = {
    slate: "bg-slate-50",
    purple: "bg-purple-50",
    amber: "bg-amber-50",
    emerald: "bg-emerald-50",
    red: "bg-red-50",
  };
  
  return (
    <div className={`rounded-md border border-slate-200 overflow-hidden`}>
      <div className={`${bgColors[color]} px-4 py-2 border-b border-slate-200 flex items-center gap-2`}>
        <Icon size={14} className="text-slate-600" />
        <span className="text-base font-bold uppercase tracking-wider text-slate-700">{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
};

export default function InsuranceAnalysis({ data }) {
  if (!data) return null;
  if (data.error) return (
    <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden">
      <div className="border-b border-slate-200 p-4 bg-gradient-to-r from-teal-50 to-emerald-50">
        <span className="text-sm font-bold uppercase tracking-wider text-teal-800">Insurance Company Analysis</span>
      </div>
      <CardContent className="p-6 text-center">
        <Clock size={32} className="mx-auto text-amber-400 mb-3" />
        <p className="text-base font-medium text-slate-700">Insurance Analysis Temporarily Unavailable</p>
        <p className="text-sm text-slate-400 mt-1">This section will be generated when the AI service is back online.</p>
      </CardContent>
    </Card>
  );
  
  const {
    executive_summary,
    insurability_score,
    insurability_grade,
    idv_calculation,
    risk_quantification,
    fraud_risk_model,
    premium_model,
    claims_impact_analysis,
  } = data;

  const getScoreColor = (score) => {
    if (score >= 80) return "emerald";
    if (score >= 60) return "amber";
    return "red";
  };

  return (
    <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden" data-testid="insurance-analysis-card">
      <div className="border-b border-slate-200 p-3 bg-gradient-to-r from-purple-50 to-pink-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-purple-600" />
          <span className="text-base font-bold uppercase tracking-wider text-purple-800">
            Insurance Company Analysis
          </span>
          <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-sm">
            UNDERWRITING VIEW
          </Badge>
        </div>
        {insurability_score && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono text-purple-600">
              Insurability: {insurability_score}/100
            </span>
            <Badge className={`text-sm ${
              insurability_score >= 80 ? 'bg-emerald-100 text-emerald-700' : 
              insurability_score >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
            }`}>
              Grade {insurability_grade}
            </Badge>
          </div>
        )}
      </div>
      
      <CardContent className="p-4 space-y-4">
        {/* Executive Summary */}
        {executive_summary && (
          <div className="p-3 bg-purple-50 border border-purple-200 rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <FileCheck size={14} className="text-purple-600" />
              <span className="text-base font-bold text-purple-800 uppercase">Underwriter Summary</span>
            </div>
            <p className="text-sm text-slate-700">{executive_summary}</p>
          </div>
        )}

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard 
            label="IDV Recommendation" 
            value={`${idv_calculation?.final_idv_recommendation_percent || 0}%`}
            subtext="of Original Value"
            icon={DollarSign}
            color={(idv_calculation?.final_idv_recommendation_percent || 0) >= 60 ? "emerald" : "amber"}
          />
          <MetricCard 
            label="Claim Prob." 
            value={`${risk_quantification?.claim_probability_model?.total_claim_prob_percent || 0}%`}
            subtext="Total Probability"
            icon={Target}
            color={getScoreColor(100 - (risk_quantification?.claim_probability_model?.total_claim_prob_percent || 50))}
          />
          <MetricCard 
            label="Premium Factor" 
            value={`${premium_model?.final_premium_multiplier || 1.0}x`}
            subtext="Multiplier"
            icon={TrendingUp}
            color={(premium_model?.final_premium_multiplier || 1) <= 1.1 ? "emerald" : "amber"}
          />
          <MetricCard 
            label="Fraud Risk" 
            value={`${fraud_risk_model?.pre_existing_damage_score || 0}/100`}
            subtext="Pre-existing Damage"
            icon={Eye}
            color={(fraud_risk_model?.pre_existing_damage_score || 0) <= 30 ? "emerald" : 
                   (fraud_risk_model?.pre_existing_damage_score || 0) <= 60 ? "amber" : "red"}
          />
        </div>

        {/* IDV Assessment */}
        <Section title="Insured Declared Value (IDV) Assessment" icon={Scale} color="purple">
          <div className="grid grid-cols-3 gap-4 mb-3">
            <div>
              <div className="text-sm text-slate-500 uppercase mb-1">Base IDV</div>
              <div className="text-2xl font-bold text-slate-700">
                {idv_calculation?.base_idv_percent}%
              </div>
            </div>
            <div>
              <div className="text-sm text-slate-500 uppercase mb-1">Condition Adj.</div>
              <div className="text-2xl font-bold text-amber-600">
                -{idv_calculation?.condition_adjustment_percent}%
              </div>
            </div>
            <div>
              <div className="text-sm text-slate-500 uppercase mb-1">Final IDV</div>
              <div className="text-2xl font-bold text-purple-700">
                {idv_calculation?.final_idv_recommendation_percent}%
              </div>
            </div>
          </div>
          
          {idv_calculation?.idv_justification && (
            <p className="text-sm text-slate-600 italic mb-3">{idv_calculation.idv_justification}</p>
          )}
          
          {idv_calculation?.idv_trend_data && (
            <div>
              <div className="text-sm text-slate-500 uppercase mb-2">IDV Trend:</div>
              <div className="flex gap-2">
                {idv_calculation.idv_trend_data.map((item, idx) => (
                  <div key={idx} className="flex-1 p-2 bg-purple-50 rounded-md text-center text-sm">
                    <div className="text-purple-400">Yr {item.year}</div>
                    <div className="font-bold text-slate-700">{item.standard_idv}%</div>
                    <div className="text-purple-600 text-xs">Cond: {item.condition_idv}%</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* Risk Quantification */}
        <Section title="Risk Profile" icon={AlertTriangle} color="amber">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 text-center">
            <div className="p-2 bg-slate-50 rounded-md">
              <div className="text-sm text-slate-500 uppercase">Base Loss Ratio</div>
              <Badge className="text-sm bg-amber-100 text-amber-700">
                {risk_quantification?.base_loss_ratio_percent}%
              </Badge>
            </div>
            <div className="p-2 bg-slate-50 rounded-md">
              <div className="text-sm text-slate-500 uppercase">Adj. Loss Ratio</div>
              <Badge className={`text-sm ${
                (risk_quantification?.condition_adjusted_loss_ratio_percent || 65) <= 60 ? 'bg-emerald-100 text-emerald-700' :
                (risk_quantification?.condition_adjusted_loss_ratio_percent || 65) <= 75 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
              }`}>
                {risk_quantification?.condition_adjusted_loss_ratio_percent}%
              </Badge>
            </div>
            <div className="p-2 bg-slate-50 rounded-md">
              <div className="text-sm text-slate-500 uppercase">Claim Prob.</div>
              <Badge className="text-sm bg-amber-100 text-amber-700">
                {risk_quantification?.claim_probability_model?.total_claim_prob_percent}%
              </Badge>
            </div>
            <div className="p-2 bg-slate-50 rounded-md">
              <div className="text-sm text-slate-500 uppercase">Avg Claim Value</div>
              <Badge className="text-sm bg-slate-200 text-slate-700">
                {risk_quantification?.expected_claim_value?.average_claim_percent_of_idv}% IDV
              </Badge>
            </div>
          </div>
          {risk_quantification?.expected_claim_value?.severity_distribution && (
            <div className="flex items-center gap-3 text-sm">
              <span className="text-slate-500">Severity:</span>
              <Badge className="bg-emerald-50 text-emerald-600">Minor: {risk_quantification.expected_claim_value.severity_distribution.minor}%</Badge>
              <Badge className="bg-amber-50 text-amber-600">Moderate: {risk_quantification.expected_claim_value.severity_distribution.moderate}%</Badge>
              <Badge className="bg-red-50 text-red-600">Major: {risk_quantification.expected_claim_value.severity_distribution.major}%</Badge>
            </div>
          )}
        </Section>

        {/* Fraud Detection */}
        <Section title="Fraud Detection & False Claims Prevention" icon={Eye} color="red">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 text-center">
            <div className="p-2 bg-slate-50 rounded-md">
              <div className="text-sm text-slate-500 uppercase">Pre-existing Score</div>
              <Badge className={`text-sm ${
                (fraud_risk_model?.pre_existing_damage_score || 0) <= 30 ? 'bg-emerald-100 text-emerald-700' :
                (fraud_risk_model?.pre_existing_damage_score || 0) <= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
              }`}>
                {fraud_risk_model?.pre_existing_damage_score}/100
              </Badge>
            </div>
            <div className="p-2 bg-slate-50 rounded-md">
              <div className="text-sm text-slate-500 uppercase">Staged Claim</div>
              <RiskBadge level={fraud_risk_model?.staged_claim_vulnerability} />
            </div>
            <div className="p-2 bg-slate-50 rounded-md">
              <div className="text-sm text-slate-500 uppercase">Inflation Risk</div>
              <RiskBadge level={fraud_risk_model?.inflation_risk} />
            </div>
            <div className="p-2 bg-slate-50 rounded-md">
              <div className="text-sm text-slate-500 uppercase">Detection Confidence</div>
              <Badge className="text-sm bg-blue-100 text-blue-700">
                {fraud_risk_model?.detection_confidence_percent}%
              </Badge>
            </div>
          </div>
          
          {fraud_risk_model?.red_flags && fraud_risk_model.red_flags.length > 0 && (
            <div>
              <div className="text-sm text-slate-500 uppercase mb-1">Red Flags:</div>
              <div className="space-y-1">
                {fraud_risk_model.red_flags.map((flag, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm text-red-600 bg-red-50 p-2 rounded-md">
                    <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                    {flag}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* Premium Model */}
        <Section title="Premium Recommendations" icon={DollarSign} color="purple">
          <div className="grid grid-cols-3 gap-4 mb-3 text-center">
            <div className="p-3 bg-purple-50 rounded-md">
              <div className="text-sm text-purple-600 uppercase">Base Factor</div>
              <div className="text-2xl font-bold text-purple-800">{premium_model?.base_premium_factor}x</div>
            </div>
            <div className="p-3 bg-purple-50 rounded-md">
              <div className="text-sm text-purple-600 uppercase">Condition Loading</div>
              <div className="text-2xl font-bold text-amber-600">{premium_model?.condition_loading_factor}x</div>
            </div>
            <div className="p-3 bg-purple-50 rounded-md">
              <div className="text-sm text-purple-600 uppercase">Final Multiplier</div>
              <div className={`text-2xl font-bold ${
                (premium_model?.final_premium_multiplier || 1) <= 1.1 ? 'text-emerald-600' : 'text-amber-600'
              }`}>
                {premium_model?.final_premium_multiplier}x
              </div>
            </div>
          </div>
          {premium_model?.premium_justification && (
            <p className="text-sm text-slate-600 italic mb-2">{premium_model.premium_justification}</p>
          )}
          {premium_model?.recommended_deductible_percent && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-500">Recommended Deductible:</span>
              <Badge className="bg-purple-100 text-purple-700">{premium_model.recommended_deductible_percent}% of IDV</Badge>
            </div>
          )}
        </Section>

        {/* Claims Impact */}
        <Section title="Claims Reduction Insights" icon={Target} color="emerald">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="p-3 bg-emerald-50 rounded-md border border-emerald-200">
              <div className="text-sm text-emerald-600 uppercase">Preventable Claims</div>
              <div className="text-xl font-bold text-emerald-700">{claims_impact_analysis?.preventable_claims_percent}%</div>
            </div>
            <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
              <div className="text-sm text-blue-600 uppercase">Early Detection Savings</div>
              <div className="text-xl font-bold text-blue-700">{claims_impact_analysis?.early_detection_savings_percent}%</div>
            </div>
            <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
              <div className="text-sm text-slate-500 uppercase">Portfolio Impact</div>
              <div className="text-sm font-bold text-slate-700">{claims_impact_analysis?.portfolio_loss_ratio_impact}</div>
            </div>
          </div>
        </Section>
      </CardContent>
    </Card>
  );
}
